import i18next from 'i18next';

import { isSoftLoggedOut } from 'reducers/Login';
import { setSettings } from 'actions/configs';
import { checkSessionNotExpiring } from 'containers/Header/components/UserMenu/SessionExpiration/SessionExpiration';
import { updateSessionKey } from 'utils';
import {
  getSavedPosTimeoutSettings,
  getSavedSetting,
} from 'reducers/configs/settings';
import { UrlControl } from 'services/UrlControl/UrlControl';
import { addWarning } from 'actions/Error';
import { ObfuscatedLocalStorage } from 'utils/ObfuscatedLocalStorage';
import { getWarehouses } from 'actions/warehouses';

import { loadPointsOfSale } from '../../actions/PointsOfSale';
import {
  attemptLogin,
  attemptPinLogin,
  hardLogout,
  attemptCookieLogin,
  getUserRights,
  softLogout,
} from '../../actions/Login';
import {
  REDUX_CLIENTCODE,
  REDUX_USERNAME,
  REDUX_PASSWORD,
  REDUX_PIN,
  REDUX_SESSIONKEY,
  REDUX_POSID,
} from '../../constants/persistence';

export default async store => {
  const get = key => JSON.parse(ObfuscatedLocalStorage.getItem(key));

  // #region logout on inactivity
  const lastTimestamp = localStorage.getItem('lastTimestamp');
  const settings = {
    timeout: getSavedSetting('pos_timeout')(store.getState()),
    settings: getSavedPosTimeoutSettings(store.getState()),
  };
  const action =
    settings.settings?.action === 'hardLogout'
      ? hardLogout()
      : softLogout({ resetPOS: false });
  const logoutOnInactivityIsEnabled = Number(settings.timeout) > 0;
  const timeSinceLastTimestamp = Date.now() - lastTimestamp;
  const timeoutMs = Number(settings.timeout) * 1e3;
  if (logoutOnInactivityIsEnabled) {
    if (lastTimestamp && timeSinceLastTimestamp > timeoutMs) {
      // Prevents auto login that immediately auto logs out after Header component is loaded and triggers logout on inactivity
      await store.dispatch(action);
      localStorage.removeItem('lastTimestamp');
      return;
    }
  }
  // #endregion

  if (UrlControl.resumeSession) {
    const { clientCode } = UrlControl.resumeSession;
    UrlControl.log.extend('resumeSession')(
      'Attempting cookie login with',
      clientCode,
    );
    store
      .dispatch(attemptCookieLogin(clientCode))
      .catch(e =>
        console.warn('Login via cookie failed (probably expired?)', e),
      );
    return;
  }

  const isLoggedOut = isSoftLoggedOut(store.getState());
  if (isLoggedOut) {
    return;
  }

  const clientCode = get(REDUX_CLIENTCODE);
  const sessionKey = get(REDUX_SESSIONKEY);
  const username = get(REDUX_USERNAME);
  const password = get(REDUX_PASSWORD);
  const pin = get(REDUX_PIN);
  const selectedPosId = localStorage.getItem(REDUX_POSID);
  if (
    sessionKey &&
    (await store.dispatch(checkSessionNotExpiring(sessionKey)))
  ) {
    updateSessionKey(clientCode, sessionKey).then(() => {
      Promise.all([
        store.dispatch([setSettings(), getUserRights(), loadPointsOfSale()]),
        selectedPosId ? Promise.resolve() : store.dispatch(getWarehouses()),
      ]);
    });
  } else if (clientCode && username) {
    if (password) {
      store
        .dispatch(attemptLogin(clientCode, username, password))
        .then(success => {
          /*
            Check specifically for true - true is returned ONLY on successful login, otherwise it returns an erply api error
            Alternatively can check if success is either not defined OR is of type Error
          */
          if (success !== true) {
            store.dispatch(hardLogout());
          }
        });
    } else if (pin) {
      store.dispatch(attemptPinLogin(pin)).then(success => {
        /*
          Check specifically for true - true is returned ONLY on successful login, otherwise it returns an erply api error
          Alternatively can check if success is either not defined OR is of type Error
        */
        if (success !== true) {
          store.dispatch(hardLogout());
        }
      });
    } else {
      store.dispatch(hardLogout()).finally(() => {
        store.dispatch(
          addWarning(
            i18next.t('login:errors.verification', {
              context: 'noCredentials',
            }),
          ),
        );
      });
    }
  }
};
